import { useCallback, useMemo, useState } from "react";

const useScheduleValidation = (schedulingData) => {
  const [formErrors, setFormErrors] = useState({});

  const isScheduleDisabled = useMemo(() => {
    const {
      studentId,
      interviewTypeId,
      form,
      editingScheduleId,
      initialFormValues
    } = schedulingData;
    
    const startDateValue = form?.startDate || "";
    const endDateValue = form?.endDate || "";
    const startTime = (form?.startTime || "").trim();
    const endTime = (form?.endTime || "").trim();
    
    if (!editingScheduleId) {
      return (
        !studentId ||
        !interviewTypeId ||
        !startDateValue ||
        !endDateValue ||
        !startTime ||
        !endTime
      );
    }
    
    if (!initialFormValues || !initialFormValues.form) {
      return true;
    }
    
    if (!studentId || !interviewTypeId || !startDateValue || !endDateValue || !startTime || !endTime) {
      return true;
    }
    
    const currentStudentId = String(studentId || "").trim();
    const currentInterviewTypeId = String(interviewTypeId || "").trim();
    const currentStartDate = String(startDateValue).trim();
    const currentStartTime = String(startTime).trim();
    const currentEndDate = String(endDateValue).trim();
    const currentEndTime = String(endTime).trim();
    
    const initialForm = initialFormValues.form;
    const initialStudentId = String(initialFormValues.studentId || "").trim();
    const initialInterviewTypeId = String(initialFormValues.interviewTypeId || "").trim();
    const initialStartDate = String(initialForm.startDate || "").trim();
    const initialStartTime = String(initialForm.startTime || "").trim();
    const initialEndDate = String(initialForm.endDate || "").trim();
    const initialEndTime = String((initialForm.endTime || "")).trim();
    const hasChanged = 
      currentStudentId !== initialStudentId ||
      currentInterviewTypeId !== initialInterviewTypeId ||
      currentStartDate !== initialStartDate ||
      currentStartTime !== initialStartTime ||
      currentEndDate !== initialEndDate ||
      currentEndTime !== initialEndTime;
    return !hasChanged;
  }, [schedulingData]);

  const validateForm = useCallback(() => {
    const errors = {};
    const { studentId, interviewTypeId, form } = schedulingData;

    const startDateValue = form.startDate || "";
    const endDateValue = form.endDate || "";

    const rules = {
      studentName: !studentId && "Please select a student",
      interviewType: !interviewTypeId && "Please select an interview type",
      startDate: !startDateValue && "Please select a start date",
      endDate: !endDateValue && "Please select an end date",
      startTime: !form.startTime?.trim() && "Please select a start time",
      endTime: !form.endTime?.trim() && "Please select an end time",
    };

    Object.entries(rules).forEach(([field, message]) => {
      if (message) errors[field] = message;
    });

    if (!errors.startDate && startDateValue) {
      const selectedStartDate = new Date(startDateValue);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (!isNaN(selectedStartDate.getTime()) && selectedStartDate < today) {
        errors.startDate = "Start date cannot be in the past";
      }
    }

    if (!errors.endDate && !errors.startDate && startDateValue && endDateValue) {
      const selectedStartDate = new Date(startDateValue);
      const selectedEndDate = new Date(endDateValue);
      if (selectedEndDate < selectedStartDate) {
        errors.endDate = "End date cannot be before start date";
      }
    }

    if (!errors.startTime && !errors.endTime && form.startTime && form.endTime && startDateValue && endDateValue) {
      if (startDateValue === endDateValue && form.startTime === form.endTime) {
        errors.endTime = "Start time and end time cannot be the same";
      } else {
        const startDateTime = new Date(`${startDateValue}T${form.startTime}:00`);
        const endDateTime = new Date(`${endDateValue}T${form.endTime}:00`);
        
        if (startDateTime >= endDateTime) {
          errors.endTime = "End date and time must be after start date and time";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [schedulingData]);

  const preparePayload = useCallback(() => {
    const startDateValue = schedulingData.form.startDate || schedulingData.form.date || "";
    const endDateValue = schedulingData.form.endDate || "";
    const startTime = schedulingData.form.startTime || "";
    const endTime = schedulingData.form.endTime || "";
    
    const activeFrom = startDateValue && startTime ? `${startDateValue}T${startTime}:00` : startDateValue;
    const activeTo = endDateValue && endTime ? `${endDateValue}T${endTime}:00` : endDateValue;
    
    const payload = {
      interview_id: schedulingData.interviewTypeId,
      student_id: schedulingData.studentId,
      active_from: activeFrom,
      active_to: activeTo,
      created_by: "",
    };

    return payload;
  }, [schedulingData]);

  return {
    formErrors,
    isScheduleDisabled,
    validateForm,
    setFormErrors,
    preparePayload,
  };
};

export default useScheduleValidation;